import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import ComparativoCoberturaIA from "./ComparativoCoberturaIA";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  BarChart3,
  Loader2,
  Search,
  Eye,
  Download,
  Calendar,
  Building2,
  User,
  Filter,
  FileText,
  Plus,
  Sparkles,
  FileSpreadsheet,
  Trash2,
  CheckSquare,
  Square,
  X,
  RefreshCw
} from "lucide-react";

export default function ComparativosBeneficiosPage() {
  const navigate = useNavigate();
  const [comparativos, setComparativos] = useState([]);
  const [aiComparativos, setAiComparativos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [aseguradoraFilter, setAseguradoraFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [aseguradoras, setAseguradoras] = useState([]);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [clientName, setClientName] = useState("");
  const [clientAge, setClientAge] = useState("");
  const [comparisonFocus, setComparisonFocus] = useState("general");
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedComparativo, setSelectedComparativo] = useState(null);
  
  // Multi-selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [deletingMultiple, setDeletingMultiple] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  
  // Current Plan Comparison states
  const [showCurrentPlanDialog, setShowCurrentPlanDialog] = useState(false);
  const [currentPlanFile, setCurrentPlanFile] = useState(null);
  const [currentPlanClientName, setCurrentPlanClientName] = useState("");
  const [currentPlanClientAge, setCurrentPlanClientAge] = useState("");
  const [currentPlanNotes, setCurrentPlanNotes] = useState("");
  const [currentPlanSelectedPlans, setCurrentPlanSelectedPlans] = useState([]);
  const [comparingCurrentPlan, setComparingCurrentPlan] = useState(false);
  const [planes, setPlanes] = useState([]);
  
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [compRes, aiCompRes, asegRes, planesRes] = await Promise.all([
        axios.get(`${API}/comparativos-beneficios`, getAuthHeaders()),
        axios.get(`${API}/comparativos-beneficios-ai`, getAuthHeaders()),
        axios.get(`${API}/aseguradoras`, getAuthHeaders()),
        axios.get(`${API}/planes-cobertura`, getAuthHeaders())
      ]);
      setComparativos(compRes.data);
      setAiComparativos(aiCompRes.data);
      setAseguradoras(asegRes.data);
      setPlanes(planesRes.data);
    } catch (err) {
      console.error("Error:", err);
      setComparativos([]);
      setAiComparativos([]);
      setAseguradoras([]);
      setPlanes([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Multi-selection functions
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedItems(new Set());
  };

  const toggleItemSelection = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const selectAllItems = () => {
    const allIds = new Set([...filteredComparativos, ...filteredAiComparativos].map(c => c.id));
    setSelectedItems(allIds);
  };

  const deselectAllItems = () => {
    setSelectedItems(new Set());
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteSelected = async () => {
    setDeletingMultiple(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of selectedItems) {
      try {
        // Try AI comparativo first, then regular
        try {
          await axios.delete(`${API}/comparativos-beneficios/ai/${id}`, getAuthHeaders());
        } catch {
          await axios.delete(`${API}/comparativos-beneficios/${id}`, getAuthHeaders());
        }
        successCount++;
      } catch (error) {
        console.error(`Error deleting comparativo ${id}:`, error);
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} comparativo(s) eliminado(s) exitosamente`);
      fetchData();
    }
    if (errorCount > 0) {
      toast.error(`No se pudieron eliminar ${errorCount} comparativo(s)`);
    }

    setSelectedItems(new Set());
    setIsSelectionMode(false);
    setDeletingMultiple(false);
    setShowDeleteConfirmDialog(false);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDateRange = (filter) => {
    const now = new Date();
    switch (filter) {
      case 'today':
        return new Date(now.setHours(0, 0, 0, 0));
      case 'week':
        return new Date(now.setDate(now.getDate() - 7));
      case 'month':
        return new Date(now.setMonth(now.getMonth() - 1));
      default:
        return null;
    }
  };

  // Get all plans from all aseguradoras
  const allPlans = aseguradoras.flatMap(a => 
    (a.planes || []).map(p => ({ ...p, aseguradora_name: a.name, aseguradora_id: a.id }))
  );

  const handlePlanToggle = (planId) => {
    setSelectedPlans(prev => 
      prev.includes(planId) 
        ? prev.filter(id => id !== planId)
        : [...prev, planId]
    );
  };

  const handleAIAnalysis = async () => {
    if (selectedPlans.length < 2) {
      toast.error("Selecciona al menos 2 planes para comparar");
      return;
    }
    if (!clientName.trim()) {
      toast.error("Ingresa el nombre del cliente");
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/comparativos-beneficios/analyze`, {
        plan_ids: selectedPlans,
        client_name: clientName,
        client_age: clientAge ? parseInt(clientAge) : null,
        comparison_focus: comparisonFocus
      }, getAuthHeaders());

      toast.success("Análisis completado exitosamente");
      setShowNewDialog(false);
      setSelectedPlans([]);
      setClientName("");
      setClientAge("");
      setComparisonFocus("general");
      fetchData();
      setSelectedComparativo(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al realizar el análisis");
    } finally {
      setAnalyzing(false);
    }
  };

  const exportToExcel = async (compId) => {
    try {
      const response = await axios.get(`${API}/comparativos-beneficios/${compId}/export-excel`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comparativo_${compId.slice(0, 8)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Excel descargado");
    } catch (err) {
      toast.error("Error al exportar a Excel");
    }
  };

  const handleCurrentPlanComparison = async () => {
    if (!currentPlanFile) {
      toast.error("Por favor, seleccione un archivo del plan actual");
      return;
    }
    if (!currentPlanClientName || !currentPlanClientAge) {
      toast.error("Por favor, complete los datos del cliente");
      return;
    }
    if (currentPlanSelectedPlans.length === 0) {
      toast.error("Por favor, seleccione al menos un plan propuesto");
      return;
    }

    setComparingCurrentPlan(true);
    const formData = new FormData();
    formData.append('current_plan_file', currentPlanFile);
    formData.append('plan_ids', currentPlanSelectedPlans.join(','));
    formData.append('client_name', currentPlanClientName);
    formData.append('client_age', currentPlanClientAge);
    formData.append('notes', currentPlanNotes);

    try {
      const res = await axios.post(`${API}/comparativos-beneficios/compare-with-current`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success("Comparación completada exitosamente");
      setShowCurrentPlanDialog(false);
      setCurrentPlanFile(null);
      setCurrentPlanClientName("");
      setCurrentPlanClientAge("");
      setCurrentPlanNotes("");
      setCurrentPlanSelectedPlans([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al realizar la comparación");
    } finally {
      setComparingCurrentPlan(false);
    }
  };

  const toggleCurrentPlanSelection = (planId) => {
    setCurrentPlanSelectedPlans(prev => 
      prev.includes(planId) ? prev.filter(id => id !== planId) : [...prev, planId]
    );
  };

  const deleteComparativo = async (compId, isAI = false) => {
    if (!window.confirm("¿Estás seguro de eliminar este comparativo?")) return;
    
    try {
      await axios.delete(`${API}/comparativos-beneficios${isAI ? '-ai' : ''}/${compId}`, getAuthHeaders());
      toast.success("Comparativo eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  // Combine regular and AI comparativos
  const allComparativos = [
    ...comparativos.map(c => ({ ...c, isAI: false })),
    ...aiComparativos.map(c => ({ ...c, isAI: true }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filteredComparativos = allComparativos.filter(c => {
    const matchesSearch = 
      c.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.aseguradora_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesAseguradora = aseguradoraFilter === "all" || c.aseguradora_id === aseguradoraFilter;
    
    let matchesDate = true;
    if (dateFilter !== "all") {
      const dateLimit = getDateRange(dateFilter);
      if (dateLimit) {
        matchesDate = new Date(c.created_at) >= dateLimit;
      }
    }
    
    return matchesSearch && matchesAseguradora && matchesDate;
  });

  // Separate filtered lists for AI and regular comparativos
  const filteredAiComparativos = filteredComparativos.filter(c => c.isAI);


  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="comparativos-beneficios-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Comparativos de Beneficios
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Historial de comparativos generados por fecha, aseguradora y cliente
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Multi-selection controls */}
            {isSelectionMode ? (
              <>
                <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-lg">
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    {selectedItems.size} seleccionados
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllItems}
                  className="text-slate-600 dark:text-slate-300"
                >
                  <CheckSquare className="w-4 h-4 mr-1" />
                  Todos
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deselectAllItems}
                  className="text-slate-600 dark:text-slate-300"
                >
                  <Square className="w-4 h-4 mr-1" />
                  Ninguno
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={selectedItems.size === 0 || deletingMultiple}
                >
                  {deletingMultiple ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Eliminar ({selectedItems.size})
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectionMode}
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline"
                  onClick={toggleSelectionMode}
                  className="text-slate-600 dark:text-slate-300"
                >
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Seleccionar
                </Button>
                <Button
                  onClick={() => navigate('/beneficios/comparar-renovaciones')}
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-600 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
                  data-testid="comparar-renovaciones-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Comparar Renovaciones
                </Button>
                <Button 
                  onClick={() => setShowNewDialog(true)}
                  variant="outline"
                  className="border-[#0C314C] text-[#0C314C] hover:bg-[#0C314C]/10"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Nuevo Comparativo IA
                </Button>
                <Button 
                  onClick={() => setShowCurrentPlanDialog(true)}
                  className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Comparar con Plan Actual
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Comparativo de Cobertura IA (lectura de PDFs de planes) */}
        <ComparativoCoberturaIA />

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{allComparativos.length}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Comparativos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{aiComparativos.length}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Con IA</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {allComparativos.filter(c => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return new Date(c.created_at) >= today;
                    }).length}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Hoy</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{aseguradoras.length}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Aseguradoras</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {[...new Set(comparativos.map(c => c.client_name))].length}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Clientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-slate-200 dark:border-slate-700 shadow-sm mb-6 dark:bg-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Buscar por cliente o aseguradora..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                  data-testid="search-comparativos"
                />
              </div>
              
              <Select value={aseguradoraFilter} onValueChange={setAseguradoraFilter}>
                <SelectTrigger className="w-full md:w-[200px] border-slate-200 dark:border-slate-600">
                  <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Aseguradora" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las aseguradoras</SelectItem>
                  {aseguradoras.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-full md:w-[180px] border-slate-200 dark:border-slate-600">
                  <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Fecha" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las fechas</SelectItem>
                  <SelectItem value="today">Hoy</SelectItem>
                  <SelectItem value="week">Última semana</SelectItem>
                  <SelectItem value="month">Último mes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Comparativos List */}
        <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
          <CardHeader className="border-b border-slate-100 dark:border-slate-700">
            <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white font-['Manrope']">
              Historial de Comparativos
            </CardTitle>
            <CardDescription className="dark:text-slate-400">
              {filteredComparativos.length} comparativo(s) encontrado(s)
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredComparativos.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400 mb-2">
                  No hay comparativos registrados
                </p>
                <p className="text-sm text-slate-400 dark:text-slate-500 mb-4">
                  Crea tu primer comparativo con análisis de IA
                </p>
                <Button 
                  onClick={() => setShowNewDialog(true)} 
                  variant="outline" 
                  className="border-slate-200 dark:border-slate-600 dark:text-slate-300"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Nuevo Comparativo IA
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredComparativos.map((comp) => (
                  <div
                    key={comp.id}
                    className={`flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${
                      isSelectionMode && selectedItems.has(comp.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                    data-testid={`comparativo-row-${comp.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {isSelectionMode && (
                        <Checkbox
                          checked={selectedItems.has(comp.id)}
                          onCheckedChange={() => toggleItemSelection(comp.id)}
                          className="w-5 h-5"
                        />
                      )}
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${comp.isAI ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-[#0C314C]'}`}>
                        {comp.isAI ? (
                          <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        ) : (
                          <BarChart3 className="w-6 h-6 text-[#45E69A]" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 dark:text-white">
                            {comp.client_name}
                          </p>
                          {comp.isAI && (
                            <Badge className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-0 text-xs">
                              IA
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {comp.aseguradora_name || (comp.plans && comp.plans[0]?.aseguradora_name) || "N/A"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(comp.created_at)}
                          </span>
                        </div>
                        {(comp.plans_compared || comp.plans) && (
                          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                            {(comp.plans_compared || comp.plans).length} planes comparados
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {comp.isAI && comp.analysis?.recomendacion && (
                        <Badge className="bg-[#45E69A]/20 text-[#0C314C] dark:text-[#45E69A] border-0">
                          Rec: {comp.analysis.recomendacion.plan_recomendado?.slice(0, 20)}...
                        </Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedComparativo(comp)}
                        className="border-slate-200 dark:border-slate-600 dark:text-slate-300"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportToExcel(comp.id)}
                        className="border-slate-200 dark:border-slate-600 dark:text-slate-300"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-1" />
                        Excel
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteComparativo(comp.id, comp.isAI)}
                        className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* New AI Comparison Dialog */}
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className="max-w-2xl dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="dark:text-white">Nuevo Comparativo con IA</DialogTitle>
              <DialogDescription className="dark:text-slate-400">
                Selecciona los planes que deseas comparar y la IA generará un análisis detallado
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">Nombre del Cliente</Label>
                  <Input
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Ej: Empresa ABC S.A."
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">Edad del Cliente</Label>
                  <Input
                    type="number"
                    value={clientAge}
                    onChange={(e) => setClientAge(e.target.value)}
                    placeholder="Ej: 35"
                    min="1"
                    max="100"
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    La edad ayuda a generar sugerencias personalizadas
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="dark:text-slate-300">Enfoque del Análisis</Label>
                <Select value={comparisonFocus} onValueChange={setComparisonFocus}>
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General (Todos los aspectos)</SelectItem>
                    <SelectItem value="coverages">Coberturas y Beneficios</SelectItem>
                    <SelectItem value="prices">Precios y Valor</SelectItem>
                    <SelectItem value="limits">Límites y Deducibles</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="dark:text-slate-300">Selecciona Planes (mínimo 2)</Label>
                <div className="border rounded-lg p-3 max-h-60 overflow-y-auto dark:border-slate-600">
                  {allPlans.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
                      No hay planes disponibles. Crea planes en la sección de Aseguradoras.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {allPlans.map((plan) => (
                        <div 
                          key={plan.id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                            selectedPlans.includes(plan.id) 
                              ? 'bg-[#0C314C]/10 dark:bg-[#0C314C]/30' 
                              : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                          }`}
                          onClick={() => handlePlanToggle(plan.id)}
                        >
                          <Checkbox 
                            checked={selectedPlans.includes(plan.id)}
                            onCheckedChange={() => handlePlanToggle(plan.id)}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-sm dark:text-white">{plan.name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{plan.aseguradora_name}</p>
                          </div>
                          {plan.premium_range && (
                            <Badge variant="outline" className="text-xs dark:border-slate-600 dark:text-slate-300">
                              {plan.premium_range}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {selectedPlans.length} plan(es) seleccionado(s)
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowNewDialog(false)}
                className="dark:border-slate-600 dark:text-slate-300"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleAIAnalysis}
                disabled={analyzing || selectedPlans.length < 2 || !clientName.trim()}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analizar con IA
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Comparativo Dialog */}
        <Dialog open={!!selectedComparativo} onOpenChange={() => setSelectedComparativo(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="dark:text-white text-xl">
                Comparativo: {selectedComparativo?.client_name}
                {selectedComparativo?.client_age && ` (${selectedComparativo.client_age} años)`}
              </DialogTitle>
              <DialogDescription className="dark:text-slate-400">
                {selectedComparativo?.created_at && formatDate(selectedComparativo.created_at)}
                {selectedComparativo?.isAI && " • Análisis con IA"}
              </DialogDescription>
            </DialogHeader>
            
            {selectedComparativo?.analysis && (
              <div className="space-y-6 py-4">
                {/* Executive Summary */}
                <div className="bg-gradient-to-r from-[#0C314C]/5 to-[#45E69A]/5 dark:from-[#0C314C]/20 dark:to-[#45E69A]/20 rounded-lg p-4 border border-[#0C314C]/20">
                  <h4 className="font-semibold text-[#0C314C] dark:text-[#45E69A] mb-2 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Resumen Ejecutivo
                  </h4>
                  <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line leading-relaxed">
                    {selectedComparativo.analysis.resumen_ejecutivo}
                  </p>
                </div>

                {/* Coverage Comparison Table */}
                {selectedComparativo.analysis.tabla_coberturas && selectedComparativo.analysis.tabla_coberturas.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-blue-500" />
                      Comparativa de Coberturas
                    </h4>
                    <div className="overflow-x-auto rounded-lg border dark:border-slate-700">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 dark:bg-slate-700">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">Cobertura</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">
                              {selectedComparativo.analysis.tabla_coberturas[0]?.plan1_nombre || "Plan 1"}
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">
                              {selectedComparativo.analysis.tabla_coberturas[0]?.plan2_nombre || "Plan 2"}
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-200">Mejor</th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-200">Conf.</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-slate-700">
                          {selectedComparativo.analysis.tabla_coberturas.map((row, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                              <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{row.cobertura}</td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{row.plan1_valor || "N/A"}</td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{row.plan2_valor || "N/A"}</td>
                              <td className="px-4 py-3 text-center">
                                <Badge className={`${
                                  row.mejor?.includes("1") ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" :
                                  row.mejor?.includes("2") ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" :
                                  "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                                }`}>
                                  {row.mejor || "Igual"}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {row.confianza && <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  row.confianza === "alto" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" :
                                  row.confianza === "medio" ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300" :
                                  "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                }`}>{row.confianza}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Limits and Deductibles Table */}
                {selectedComparativo.analysis.tabla_limites_deducibles && selectedComparativo.analysis.tabla_limites_deducibles.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-purple-500" />
                      Límites y Deducibles
                    </h4>
                    <div className="overflow-x-auto rounded-lg border dark:border-slate-700">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 dark:bg-slate-700">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">Concepto</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">Plan 1</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-200">Plan 2</th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-200">Mejor</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-slate-700">
                          {selectedComparativo.analysis.tabla_limites_deducibles.map((row, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                              <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{row.concepto}</td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{row.plan1_valor || "N/A"}</td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{row.plan2_valor || "N/A"}</td>
                              <td className="px-4 py-3 text-center">
                                <Badge className={`${
                                  row.mejor?.includes("1") ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" :
                                  row.mejor?.includes("2") ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" :
                                  "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                                }`}>
                                  {row.mejor || "Igual"}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Recommendation */}
                {selectedComparativo.analysis.recomendacion && (
                  <div className="bg-gradient-to-r from-[#45E69A]/10 to-[#45E69A]/5 dark:from-[#45E69A]/20 dark:to-[#45E69A]/10 rounded-lg p-5 border border-[#45E69A]/30">
                    <h4 className="font-semibold text-[#0C314C] dark:text-[#45E69A] mb-3 flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      Plan Recomendado
                    </h4>
                    <p className="font-bold text-xl text-slate-900 dark:text-white mb-2">
                      {selectedComparativo.analysis.recomendacion.plan_recomendado}
                    </p>
                    {selectedComparativo.analysis.recomendacion.razon_principal && (
                      <div className="bg-white/50 dark:bg-slate-800/50 rounded-lg p-3 mb-3">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          <span className="text-[#0C314C] dark:text-[#45E69A]">Razón principal:</span> {selectedComparativo.analysis.recomendacion.razon_principal}
                        </p>
                      </div>
                    )}
                    {selectedComparativo.analysis.recomendacion.ventajas_clave && (
                      <div className="mb-3">
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Ventajas clave:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedComparativo.analysis.recomendacion.ventajas_clave.map((v, i) => (
                            <Badge key={i} className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                              ✓ {v}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedComparativo.analysis.recomendacion.consideraciones && (
                      <div>
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">Consideraciones:</p>
                        <ul className="text-sm text-slate-600 dark:text-slate-300 list-disc list-inside space-y-1">
                          {selectedComparativo.analysis.recomendacion.consideraciones.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Age-based Suggestions */}
                {selectedComparativo.analysis.sugerencias_por_edad && selectedComparativo.analysis.sugerencias_por_edad.edad_cliente > 0 && (
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-5 border border-purple-200 dark:border-purple-800">
                    <h4 className="font-semibold text-purple-700 dark:text-purple-300 mb-3 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Sugerencias según Edad ({selectedComparativo.analysis.sugerencias_por_edad.edad_cliente} años - {selectedComparativo.analysis.sugerencias_por_edad.rango_edad})
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedComparativo.analysis.sugerencias_por_edad.coberturas_prioritarias && (
                        <div className="bg-white/50 dark:bg-slate-800/50 rounded-lg p-3">
                          <p className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-2">Coberturas Prioritarias:</p>
                          <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
                            {selectedComparativo.analysis.sugerencias_por_edad.coberturas_prioritarias.map((c, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                                {c}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {selectedComparativo.analysis.sugerencias_por_edad.riesgos_comunes && (
                        <div className="bg-white/50 dark:bg-slate-800/50 rounded-lg p-3">
                          <p className="text-sm font-semibold text-orange-600 dark:text-orange-400 mb-2">Riesgos Comunes:</p>
                          <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
                            {selectedComparativo.analysis.sugerencias_por_edad.riesgos_comunes.map((r, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                                {r}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    {selectedComparativo.analysis.sugerencias_por_edad.recomendaciones_adicionales && (
                      <div className="mt-3 bg-white/50 dark:bg-slate-800/50 rounded-lg p-3">
                        <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-2">Recomendaciones Adicionales:</p>
                        <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
                          {selectedComparativo.analysis.sugerencias_por_edad.recomendaciones_adicionales.map((r, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <span className="text-blue-500">→</span>
                              {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {selectedComparativo.analysis.sugerencias_por_edad.plan_mas_adecuado_edad && (
                      <div className="mt-3 p-3 bg-gradient-to-r from-green-100 to-green-50 dark:from-green-900/30 dark:to-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <p className="text-sm">
                          <span className="font-semibold text-green-700 dark:text-green-300">Plan más adecuado para su edad: </span>
                          <span className="font-bold text-green-800 dark:text-green-200">{selectedComparativo.analysis.sugerencias_por_edad.plan_mas_adecuado_edad}</span>
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Key Differences */}
                {selectedComparativo.analysis.diferencias_clave && selectedComparativo.analysis.diferencias_clave.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Diferencias Clave</h4>
                    <div className="space-y-2">
                      {selectedComparativo.analysis.diferencias_clave.map((diff, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                          <Badge 
                            className={`shrink-0 ${
                              diff.importancia === 'alta' 
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' 
                                : diff.importancia === 'media'
                                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                                  : 'bg-slate-100 text-slate-700 dark:bg-slate-600 dark:text-slate-300'
                            }`}
                          >
                            {diff.importancia?.toUpperCase()}
                          </Badge>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white text-sm">{diff.diferencia}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{diff.impacto}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Best Practices */}
                {selectedComparativo.analysis.mejores_practicas && selectedComparativo.analysis.mejores_practicas.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Mejores Prácticas</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-300">
                      {selectedComparativo.analysis.mejores_practicas.map((practice, idx) => (
                        <li key={idx}>{practice}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Score / Puntaje Comparativo */}
                {selectedComparativo.analysis.puntaje && (
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Puntaje Comparativo</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {["plan1", "plan2"].map((pk, i) => {
                        const s = selectedComparativo.analysis.puntaje[pk];
                        if (!s) return null;
                        const planName = selectedComparativo.plans?.[i]?.name || `Plan ${i + 1}`;
                        return (
                          <div key={pk} className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg" data-testid={`score-${pk}`}>
                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">{planName}</p>
                            <div className="text-3xl font-bold text-blue-700 dark:text-blue-400 mb-3">{s.total || 0}<span className="text-sm text-slate-400">/100</span></div>
                            {[{label: "Cobertura", key: "cobertura", color: "bg-green-500"},
                              {label: "Precio", key: "precio", color: "bg-blue-500"},
                              {label: "Red Médica", key: "red_medica", color: "bg-purple-500"},
                              {label: "Condiciones", key: "condiciones", color: "bg-amber-500"}
                            ].map(item => s[item.key] !== undefined && (
                              <div key={item.key} className="mb-2">
                                <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                                  <span>{item.label}</span><span>{s[item.key]}/100</span>
                                </div>
                                <div className="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-2">
                                  <div className={`${item.color} h-2 rounded-full`} style={{width: `${s[item.key]}%`}} />
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                    {selectedComparativo.analysis.puntaje.explicacion && (
                      <p className="mt-3 text-sm text-slate-500 dark:text-slate-400 italic">{selectedComparativo.analysis.puntaje.explicacion}</p>
                    )}
                  </div>
                )}

                {/* Financial Comparison */}
                {selectedComparativo.analysis.comparacion_financiera && (
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Comparación Financiera</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-center">
                        <p className="text-xs text-blue-600 dark:text-blue-400">Prima Plan A</p>
                        <p className="text-lg font-bold text-blue-800 dark:text-blue-200">{selectedComparativo.analysis.comparacion_financiera.prima_plan1 || "N/A"}</p>
                      </div>
                      <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg text-center">
                        <p className="text-xs text-green-600 dark:text-green-400">Prima Plan B</p>
                        <p className="text-lg font-bold text-green-800 dark:text-green-200">{selectedComparativo.analysis.comparacion_financiera.prima_plan2 || "N/A"}</p>
                      </div>
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg text-center">
                        <p className="text-xs text-purple-600 dark:text-purple-400">Diferencia</p>
                        <p className="text-lg font-bold text-purple-800 dark:text-purple-200">{selectedComparativo.analysis.comparacion_financiera.diferencia || "N/A"}</p>
                      </div>
                    </div>
                    {selectedComparativo.analysis.comparacion_financiera.costo_beneficio && (
                      <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Análisis Costo/Beneficio:</p>
                        <p className="text-sm text-slate-700 dark:text-slate-300">{selectedComparativo.analysis.comparacion_financiera.costo_beneficio}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Risk Assessment */}
                {selectedComparativo.analysis.evaluacion_riesgos && (
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Evaluación de Riesgos</h4>
                    <div className="space-y-3">
                      {selectedComparativo.analysis.evaluacion_riesgos.riesgos_bien_cubiertos?.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold text-green-700 dark:text-green-400 mb-1">Riesgos Bien Cubiertos</p>
                          <ul className="space-y-1">{selectedComparativo.analysis.evaluacion_riesgos.riesgos_bien_cubiertos.map((r, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300"><span className="text-green-500 mt-0.5">&#10003;</span>{r}</li>
                          ))}</ul>
                        </div>
                      )}
                      {selectedComparativo.analysis.evaluacion_riesgos.gaps_cobertura?.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold text-red-700 dark:text-red-400 mb-1">Gaps de Cobertura</p>
                          <ul className="space-y-1">{selectedComparativo.analysis.evaluacion_riesgos.gaps_cobertura.map((g, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300"><span className="text-red-500 mt-0.5">!</span>{g}</li>
                          ))}</ul>
                        </div>
                      )}
                      {selectedComparativo.analysis.evaluacion_riesgos.sugerencias_complementarias?.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-1">Sugerencias Complementarias</p>
                          <ul className="space-y-1">{selectedComparativo.analysis.evaluacion_riesgos.sugerencias_complementarias.map((s, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300"><span className="text-blue-500">→</span>{s}</li>
                          ))}</ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* International Health */}
                {selectedComparativo.analysis.salud_internacional && selectedComparativo.analysis.salud_internacional.aplica && (
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Salud Internacional</h4>
                    <div className="space-y-2 text-sm">
                      {[{label: "Territorio Plan A", value: selectedComparativo.analysis.salud_internacional.territorio_plan1},
                        {label: "Territorio Plan B", value: selectedComparativo.analysis.salud_internacional.territorio_plan2},
                        {label: "Evacuación Médica", value: selectedComparativo.analysis.salud_internacional.evacuacion_medica},
                        {label: "Segunda Opinión", value: selectedComparativo.analysis.salud_internacional.segunda_opinion},
                        {label: "Portabilidad", value: selectedComparativo.analysis.salud_internacional.portabilidad}
                      ].filter(item => item.value).map((item, i) => (
                        <div key={i} className="flex gap-2 p-2 bg-slate-50 dark:bg-slate-700 rounded">
                          <span className="font-semibold text-slate-700 dark:text-slate-300 shrink-0">{item.label}:</span>
                          <span className="text-slate-600 dark:text-slate-400">{item.value}</span>
                        </div>
                      ))}
                      {selectedComparativo.analysis.salud_internacional.nota && (
                        <p className="text-xs italic text-slate-500 dark:text-slate-400 mt-2">{selectedComparativo.analysis.salud_internacional.nota}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!selectedComparativo?.analysis && selectedComparativo?.plans_compared && (
              <div className="py-4">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Planes Comparados</h4>
                <div className="space-y-2">
                  {selectedComparativo.plans_compared.map((plan, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                      <p className="font-medium dark:text-white">{plan.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{plan.description || "Sin descripción"}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => exportToExcel(selectedComparativo?.id)}
                className="dark:border-slate-600 dark:text-slate-300"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Exportar Excel
              </Button>
              <Button 
                onClick={() => setSelectedComparativo(null)}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Current Plan Comparison Dialog */}
        <Dialog open={showCurrentPlanDialog} onOpenChange={setShowCurrentPlanDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope']">
                Comparar con Plan Actual
              </DialogTitle>
              <DialogDescription>
                Sube el documento del plan actual del cliente y selecciona los planes propuestos para comparar
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto space-y-6 py-4">
              {/* File Upload */}
              <div className="space-y-2">
                <Label>Documento del Plan Actual *</Label>
                <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setCurrentPlanFile(e.target.files[0])}
                    className="hidden"
                    id="current-plan-file"
                  />
                  <label htmlFor="current-plan-file" className="cursor-pointer">
                    {currentPlanFile ? (
                      <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
                        <FileText className="w-6 h-6" />
                        <span className="font-medium">{currentPlanFile.name}</span>
                      </div>
                    ) : (
                      <>
                        <FileText className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                        <p className="text-slate-600 dark:text-slate-400 mb-1">Click para subir documento</p>
                        <p className="text-xs text-slate-400">PDF, JPG o PNG (máx 10MB)</p>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="current-client-name">Nombre del Cliente *</Label>
                  <Input
                    id="current-client-name"
                    value={currentPlanClientName}
                    onChange={(e) => setCurrentPlanClientName(e.target.value)}
                    placeholder="Nombre completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="current-client-age">Edad *</Label>
                  <Input
                    id="current-client-age"
                    type="number"
                    value={currentPlanClientAge}
                    onChange={(e) => setCurrentPlanClientAge(e.target.value)}
                    placeholder="35"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="current-notes">Notas Adicionales</Label>
                <Input
                  id="current-notes"
                  value={currentPlanNotes}
                  onChange={(e) => setCurrentPlanNotes(e.target.value)}
                  placeholder="Información adicional para la comparación"
                />
              </div>

              {/* Plan Selection */}
              <div className="space-y-3">
                <Label className="text-base">Planes Propuestos a Comparar *</Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Selecciona los planes con los que deseas comparar el plan actual
                </p>
                
                {planes.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay planes disponibles</p>
                    <p className="text-xs">Crea planes en la sección "Planes" primero</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto border rounded-lg p-3 dark:border-slate-700">
                    {planes.map(plan => (
                      <div
                        key={plan.id}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        <Checkbox
                          checked={currentPlanSelectedPlans.includes(plan.id)}
                          onCheckedChange={() => toggleCurrentPlanSelection(plan.id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 dark:text-white">{plan.name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {plan.tipo_plan} - {plan.vigencia}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {plan.moneda}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                {currentPlanSelectedPlans.length > 0 && (
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {currentPlanSelectedPlans.length} plan(es) seleccionado(s)
                  </p>
                )}
              </div>
            </div>
            
            <DialogFooter className="border-t dark:border-slate-700 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowCurrentPlanDialog(false)}
                disabled={comparingCurrentPlan}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleCurrentPlanComparison}
                disabled={comparingCurrentPlan}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {comparingCurrentPlan && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {comparingCurrentPlan ? "Comparando..." : "Realizar Comparación"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-slate-900 dark:text-white">
                Confirmar eliminación
              </DialogTitle>
              <DialogDescription>
                ¿Estás seguro de que deseas eliminar {selectedItems.size} comparativo(s)? 
                Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirmDialog(false)}
                disabled={deletingMultiple}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteSelected}
                disabled={deletingMultiple}
              >
                {deletingMultiple ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar {selectedItems.size}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
